import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { authService } from '@/lib/auth/authService';
import { User, Mail, Phone, Settings, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/PageHeader';
import LoadingSpinner from '@/components/LoadingSpinner';
import AuthModal from '@/components/auth/AuthModal';
import ChangePasswordForm from '@/components/ChangePasswordForm';

export default function UserProfile() {
  const [userData, setUserData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [location, navigate] = useLocation();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const authenticated = authService.isAuthenticated();
    setIsAuthenticated(authenticated);

    if (authenticated) {
      const user = authService.getCurrentUser();
      setUserData(user);
    }

    setIsLoading(false);
  };

  const handleSuccessfulAuth = () => {
    setIsAuthModalOpen(false);
    checkAuthStatus();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <div className="container max-w-6xl mx-auto px-4 py-12">
        <div className="bg-zinc-900/50 backdrop-blur-sm rounded-xl border border-white/10 p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Acceso requerido</h2>
          <p className="text-zinc-400 mb-6">
            Debes iniciar sesión para acceder a tu perfil y ver precios exclusivos.
          </p>
          <Button
            onClick={() => setIsAuthModalOpen(true)}
            className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-bold"
          >
            Iniciar sesión
          </Button>
          <AuthModal
            isOpen={isAuthModalOpen}
            onClose={() => setIsAuthModalOpen(false)}
            onSuccessfulAuth={handleSuccessfulAuth}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto px-4 py-12">
      <PageHeader
        title="Mi Perfil"
        subtitle="Área personal"
        description="Gestiona tu perfil y accede a precios exclusivos para usuarios registrados."
        centered={false}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Panel de información del usuario */}
        <div className="bg-zinc-900/50 backdrop-blur-sm rounded-xl border border-white/10 p-6 md:col-span-1">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-[#FCAA0D]" />
            Información personal
          </h2>

          <div className="space-y-4">
            <div className="flex flex-col">
              <span className="text-zinc-500 text-sm mb-1">Nombre</span>
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">
                  {userData?.user_metadata?.name || 'Usuario'}
                </span>
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-zinc-500 text-sm mb-1">Correo electrónico</span>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#FCAA0D]" />
                <span className="text-white">{userData?.email}</span>
              </div>
            </div>

            {userData?.user_metadata?.phone && (
              <div className="flex flex-col">
                <span className="text-zinc-500 text-sm mb-1">Teléfono</span>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#FCAA0D]" />
                  <span className="text-white">{userData.user_metadata.phone}</span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8">
            <Button variant="outline" className="w-full justify-start gap-2">
              <Settings className="w-4 h-4" />
              Editar perfil
            </Button>
          </div>
        </div>

        {/* Panel de beneficios */}
        <div className="bg-zinc-900/50 backdrop-blur-sm rounded-xl border border-white/10 p-6 md:col-span-2">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Package className="w-5 h-5 text-[#FCAA0D]" />
            Beneficios exclusivos
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-zinc-800/50 rounded-lg p-5 border border-white/5 hover:border-[#FCAA0D]/30 transition-colors">
              <div className="text-[#FCAA0D] font-bold text-lg mb-2">Precios exclusivos</div>
              <p className="text-zinc-400 text-sm mb-4">
                Accede a precios especiales en toda nuestra gama de productos.
              </p>
              <Button
                variant="outline"
                className="w-full bg-white/5"
                onClick={() => navigate('/new')}
              >
                Ver productos
              </Button>
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-5 border border-white/5 hover:border-[#FCAA0D]/30 transition-colors">
              <div className="text-[#FCAA0D] font-bold text-lg mb-2">Soporte prioritario</div>
              <p className="text-zinc-400 text-sm mb-4">
                Disfruta de asistencia técnica prioritaria para tus consultas.
              </p>
              <Button
                variant="outline"
                className="w-full bg-white/5"
                onClick={() => navigate('/contacto')}
              >
                Contactar
              </Button>
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-5 border border-white/5 hover:border-[#FCAA0D]/30 transition-colors">
              <div className="text-[#FCAA0D] font-bold text-lg mb-2">Descuentos por volumen</div>
              <p className="text-zinc-400 text-sm mb-4">
                Obtén descuentos especiales en compras por volumen.
              </p>
              <Button
                variant="outline"
                className="w-full bg-white/5"
              >
                Ver condiciones
              </Button>
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-5 border border-white/5 hover:border-[#FCAA0D]/30 transition-colors">
              <div className="text-[#FCAA0D] font-bold text-lg mb-2">Catálogos exclusivos</div>
              <p className="text-zinc-400 text-sm mb-4">
                Accede a catálogos y manuales disponibles solo para usuarios registrados.
              </p>
              <Button
                variant="outline"
                className="w-full bg-white/5"
                onClick={() => navigate('/catalogos')}
              >
                Ver catálogos
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sección de cambio de contraseña */}
      <div className="mt-8 bg-zinc-900/50 backdrop-blur-sm rounded-xl border border-white/10 p-6">
        <ChangePasswordForm />
      </div>

      {/* Historial de actividad reciente */}
      <div className="mt-8 bg-zinc-900/50 backdrop-blur-sm rounded-xl border border-white/10 p-6">
        <h2 className="text-xl font-bold text-white mb-6">Actividad reciente</h2>

        <div className="text-center py-10">
          <p className="text-zinc-400">
            Aún no hay actividad reciente en tu cuenta.
          </p>
        </div>
      </div>
    </div>
  );
}
